import { PRICING_TYPES, SKU_TYPES } from '@constants';
import {
  fixedNumber,
  formatAmount,
  formatDate,
  formatVehicleStage,
} from '@utils';
import {
  getAvailability,
  getBodySize,
  getBodyType,
  getCategory,
  getFuelType,
  getProductLines,
} from 'store/catalog/vehicles.helpers';

import {
  DEFAULT_DROPDOWN,
  EBIKES_SIZES,
  EBIKES_TYPES,
  STATUSES,
} from './constants';

const handleModelData = (models) => {
  if (!models || typeof models !== 'object') {
    return {};
  }

  const data = {};

  const uniqueManufacturers = [];

  const uniqueModels = [];

  const modelObj = {};

  Object.keys(models)?.forEach((dataManufacturer) => {
    const exists = uniqueManufacturers?.some((manufacturer) => {
      return manufacturer.value === models[dataManufacturer].slug;
    });

    if (exists) return;
    uniqueManufacturers?.push({
      value: models[dataManufacturer].slug,
      text: models[dataManufacturer].name,
    });
    Object.keys(models[dataManufacturer]?.models)?.forEach((model) => {
      const uniqueModelExists = uniqueModels?.some((uniqueModel) => {
        return (
          uniqueModel.value === models[dataManufacturer]?.models[model].slug &&
          uniqueModel.group === models[dataManufacturer]?.slug
        );
      });

      if (uniqueModelExists) return;
      if (modelObj[models[dataManufacturer]?.slug]) {
        modelObj[models[dataManufacturer]?.slug]?.push({
          value: models[dataManufacturer]?.models[model].slug,
          text: models[dataManufacturer]?.models[model].name,
          group: models[dataManufacturer]?.slug,
        });
      } else {
        modelObj[models[dataManufacturer]?.slug] = [
          {
            value: models[dataManufacturer]?.models[model].slug,
            text: models[dataManufacturer]?.models[model].name,
            group: models[dataManufacturer]?.slug,
          },
        ];
      }
      uniqueModels?.push({
        value: models[dataManufacturer]?.models[model].slug,
        text: models[dataManufacturer]?.models[model].name,
        group: models[dataManufacturer]?.slug,
      });
    });
  });

  data.models = uniqueModels;
  data.manufacturers = uniqueManufacturers;
  modelObj[undefined] = [];
  data.modelObj = modelObj;
  return data;
};

const handleEbikes = (array) => {
  if (!array) {
    return [];
  }

  const newArray = [...array];

  array?.forEach((item, index) => {
    const itemPrice = item.pricing?.find((pricingItem) => {
      return pricingItem?.type === PRICING_TYPES.open;
    });

    const row = {
      availability: item.availability || '-',
      brand: item?.properties?.brand || '-',
      category: item?.properties?.category || '-',
      riskCategory: item?.properties?.riskCategory || '-',
      categoryClass: item?.properties?.categoryClass || '-',
      edition: item?.properties?.edition?.trim() || '-',
      year: item?.properties?.year || '-',
      engineDisplacement: item?.properties?.engine?.displacement,
      fuelType: item?.properties?.engine?.fuelType || '-',
      image: item?.image?.url ? item.image?.url : '',
      initialPrice: itemPrice.initialPrice
        ? formatAmount(fixedNumber(itemPrice.initialPrice))
        : '-',
      instastart: itemPrice.instastart
        ? formatAmount(fixedNumber(itemPrice.instastart))
        : '-',
      kilometers: item.kilometers || '-',
      model: item?.properties?.model || '-',
      monthlyKilometers: item.monthlyKilometers || '-',
      name: item.name || '-',
      price: itemPrice.price ? formatAmount(fixedNumber(itemPrice.price)) : '-',
      status: item.published ? 'Published' : 'Unpublished',
      published: item.published,
      skuId: item.skuId || '-',
      tags: item.tags,
      transmission: item?.properties?.transmission || '-',
      updatedAt: item.updatedAt
        ? formatDate(item.updatedAt, 'dd/MM/yyyy')
        : '-',
      vendors: item.vendors,
      id: item.skuId,
      bodyType: item?.properties?.body?.type || '-',
      type: item?.type || '-',
      powerHp: item?.properties?.engine?.powerHp || '-',
      batterySize: item?.properties?.engine?.batterySize || '-',
      euroClass: item?.properties?.engine?.euroClass || '-',
      signUpFee: formatAmount(fixedNumber(item.signUpFee)) || '-',
    };
    newArray[index] = row;

    return newArray;
  });
  return newArray;
};

const handleEnums = (enumsObj) => {
  const returnObject = { ...enumsObj };

  returnObject.categoryOptions = [
    DEFAULT_DROPDOWN,
    ...(enumsObj?.categories?.map((item) => ({
      text: item.name,
      value: item.id,
    })) ?? []),
  ];

  returnObject.statusOptions = STATUSES;

  returnObject.brandOptions = [
    DEFAULT_DROPDOWN,
    ...(handleModelData(enumsObj?.models)?.manufacturers ?? []),
  ];

  return returnObject;
};

const handleEbikeData = ({ enums, ebike }) => {
  const openPrice = ebike?.pricing?.find(
    (price) => price?.type === PRICING_TYPES.open
  );
  const data = {
    ...ebike,
    type:
      SKU_TYPES[ebike.type]?.value >= 0
        ? SKU_TYPES[ebike.type]?.value
        : ebike.type,
    primaryImageUrl: ebike?.selectedImageId
      ? ebike.images?.find((image) => image?.id === ebike.selectedImageId)?.url
      : ebike.images?.[0]?.url,
    price: openPrice?.price,
    signUpFee: openPrice?.signUpFee,
    initialPrice: openPrice?.initialPrice,
    skuId: ebike?.skuId?.slice(3),
    properties: {
      ...ebike?.properties,
      category: getCategory(enums, ebike?.properties?.category),
      productLine: getProductLines(enums, ebike?.properties?.productLine),
      engine: {
        ...ebike?.properties?.engine,
        fuelType: getFuelType(enums, ebike?.properties?.engine?.fuelType),
      },
      body: {
        ...ebike?.properties?.body,
        type: getBodyType(enums, ebike?.properties?.body?.type),
        size: getBodySize(enums, ebike?.properties?.body?.size),
      },
    },
    availability: getAvailability(enums, ebike?.availability),
    specialOffer: ebike?.tags?.includes('offer'),
    tags: ebike?.tags?.filter((item) => item !== 'offer'),
  };
  return data;
};

const handleEbikesBySku = (data = []) =>
  data.map((item) => ({
    id: item.id || '-',
    internalId: item.internalId || '-',
    plate: item.plate || '-',
    brand: item.sku?.brand || '-',
    stage: formatVehicleStage(item.procurement.currentStageId),
    finalPurchasePrice: item.procurement.finalPurchasePrice
      ? formatAmount(item.procurement.finalPurchasePrice.number)
      : '-',
  }));

const getOptions = (data = [], valueKey = 'id', textKey = 'name') =>
  data?.map((item) => {
    return {
      text: item[textKey],
      value: item[valueKey],
    };
  });

const handleEbikesEnums = (data = {}, models = {}) => {
  const availabilities = getOptions(data?.availabilities);
  const fuelTypes = getOptions(data?.fuelTypes);
  const allowedMonthlyKm = getOptions(data?.allowedMonthlyKm);
  const bodyTypes = getOptions(data?.bodyTypes).filter((item) =>
    EBIKES_TYPES.includes(item.text)
  );
  const bodySizes = getOptions(data?.bodySizes).filter((item) =>
    EBIKES_SIZES.includes(item.text)
  );
  const productLines = getOptions(data?.productLines);
  const categories = getOptions(data?.categories);
  const engineTypes = getOptions(data?.engineTypes);
  const manufacturers = Object.keys(models || {})?.map((key) => {
    return {
      text: models[key].name,
      value: models[key].slug,
      models:
        Object.keys(models[key]?.models || {})?.map((modelKey) => ({
          text: models[key]?.models[modelKey].name,
          value: models[key]?.models[modelKey].slug,
        })) || [],
    };
  });

  return {
    ...data,
    availabilities,
    fuelTypes,
    allowedMonthlyKm,
    bodyTypes,
    bodySizes,
    productLines,
    categories,
    engineTypes,
    manufacturers,
  };
};

export {
  handleEbikes,
  handleEnums,
  handleEbikeData,
  handleEbikesBySku,
  handleEbikesEnums,
};
